import React, { useState, useCallback, useContext, useMemo, useRef, useEffect, forwardRef, useImperativeHandle } from "react";
import { IframeRuntime, IframeRuntimeImperativeAPI } from "./iframe-runtime";
import { DialogOverlay } from "./dialog-overlay";
import {
  ICloseModal, INavigationOptions,
  ICustomMessage, IShowDialog, IShowLightbox, IShowModal, ISupportedFeatures, IAttachmentUrlRequest, IAttachmentUrlResponse, IGetInteractiveState
} from "@concord-consortium/lara-interactive-api";
import classNames from "classnames";
import { nanoid } from "../../../utilities/nanoid";

import { PortalDataContext } from "../../portal-data-context";
import { useSizeAndAspectRatio } from "../../../hooks/use-aspect-ratio";
import { useSize } from "../../../hooks/use-size";
import { IManagedInteractive, IMwInteractive, IExportableAnswerMetadata, ILegacyLinkedInteractiveState, QuestionFeedback } from "../../../types";
import { createOrUpdateAnswer, watchAnswer, getLegacyLinkedInteractiveInfo, getAnswer, watchQuestionLevelFeedback } from "../../../firebase-db";
import { handleGetFirebaseJWT } from "../../../portal-utils";
import { getAnswerWithMetadata, getInteractiveInfo, hasLegacyLinkedInteractive, IInteractiveInfo, isQuestion, refIdToAnswersQuestionId } from "../../../utilities/embeddable-utils";
import { safeJsonParseIfString } from "../../../utilities/safe-json-parse";
import { Lightbox } from "./lightbox";
import { Logger, LogEventName } from "../../../lib/logger";
import { handleGetAttachmentUrl } from "@concord-consortium/interactive-api-host";
import { LaraDataContext } from "../../lara-data-context";
import { ClickToPlay } from "./click-to-play";
import { ManagedInteractiveHeader } from "./managed-interactive-header";
import { ManagedInteractiveHint } from "./managed-interactive-hint";
import { ActivityLayouts, displayedQuestionNumber, hasPluginThatRequiresHeader } from "../../../utilities/activity-utils";
import { useQuestionInfoContext } from "../../question-info-context";
import { isOfferingLocked } from "../../../utilities/portal-data-utils";
import { applyOverridesToAuthoredState } from "../../../utilities/url-overrides/state";
import { forwardInteractiveLog } from "../../chat/chat-log-forwarder";
import { GateEvent } from "../../../utilities/disabled-questions";

import "./managed-interactive.scss";

interface IProps {
  embeddable: IManagedInteractive | IMwInteractive;
  questionNumber?: number;
  setSupportedFeatures: (container: HTMLElement, features: ISupportedFeatures) => void;
  setSendCustomMessage: (sender: (message: ICustomMessage) => void) => void;
  setNavigation?: (options: INavigationOptions) => void;
  ref?: React.Ref<ManagedInteractiveImperativeAPI>;
  emitInteractiveAvailable?: () => void;
  showQuestionPrefix: boolean;
  hideQuestionNumbers?: boolean;
  saveInteractiveStateHistory?: boolean;
  /** Out of reach of pointer, keyboard and assistive technology, except for its heading. */
  disabled?: boolean;
  /** Its heading says the question is locked. */
  locked?: boolean;
  /** Set for a gating item: receives its gate events. */
  onQuestionGateEvent?: (event: GateEvent) => void;
}

export interface ManagedInteractiveImperativeAPI {
  saveInteractiveStateBeforeClose: (options?: IGetInteractiveState) => Promise<void>;
}

export interface IClickToPlayOptions {
  prompt?: string | null;
  imageUrl?: string | null;
}

export const ManagedInteractive: React.ForwardRefExoticComponent<IProps> = forwardRef((props, ref) => {
  const { embeddable, questionNumber, setSupportedFeatures, setSendCustomMessage, setNavigation, saveInteractiveStateHistory, disabled, locked,
    onQuestionGateEvent } = props;
  const { scrollToQuestionId } = useQuestionInfoContext();
  const portalData = useContext(PortalDataContext);
  const laraData = useContext(LaraDataContext);

  const [activeDialog, setActiveDialog] = useState<IShowDialog | null>(null);
  const [activeLightbox, setActiveLightbox] = useState<IShowLightbox | null>(null);
  const [ARFromSupportedFeatures, setARFromSupportedFeatures] = useState(0);
  const [clickedToPlay, setClickedToPlay] = useState(false);
  const [clickToPlayOptions, setClickToPlayOptions] = useState<IClickToPlayOptions|undefined>(undefined);
  const [feedback, setFeedback] = useState<QuestionFeedback | null>(null);
  const [hint, setHint] = useState("");
  const [heightFromInteractive, setHeightFromInteractive] = useState(0);
  const [screenHeight, setScreenHeight] = useState({dynamicHeight: window.innerHeight});
  const [showHint, setShowHint] = useState(false);
  const shouldWatchAnswer = isQuestion(props.embeddable, {ignoreHideQuestionNumber: true});
  const [loadingAnswer, setLoadingAnswer] = useState(shouldWatchAnswer);

  const iframeRuntimeRef = useRef<IframeRuntimeImperativeAPI>(null);
  const interactiveState = useRef<any>();
  const legacyLinkedInteractiveState = useRef<ILegacyLinkedInteractiveState | null>(null);
  const answerMeta = useRef<IExportableAnswerMetadata>();
  const shouldLoadLegacyLinkedInteractiveState = hasLegacyLinkedInteractive(props.embeddable, laraData);
  const [loadingLegacyLinkedInteractiveState, setLoadingLegacyLinkedInteractiveState] = useState(shouldLoadLegacyLinkedInteractiveState);
  const interactiveInfo = useRef<IInteractiveInfo | undefined>(undefined);
  const headerTarget = React.useRef(null);
  const hintTriggerRef = useRef<HTMLButtonElement>(null);
  const divTarget = React.useRef<HTMLDivElement>(null);
  const divSize = useSize(divTarget);
  const headerSize = useSize(headerTarget);
  const interactiveStateHistoryIdRef = useRef(nanoid());

  const embeddableRefId = embeddable.ref_id;
  const onQuestionGateEventRef = useRef(onQuestionGateEvent);
  onQuestionGateEventRef.current = onQuestionGateEvent;

  const hasPluginRequiringHeader = useMemo(() => {
    return !!laraData.activity && hasPluginThatRequiresHeader(laraData.activity, embeddableRefId);
  }, [laraData.activity, embeddableRefId]);

  useEffect(() => {
    window.addEventListener("resize", () => setScreenHeight({dynamicHeight: window.innerHeight}));
    return(() => {
        window.removeEventListener("resize", () => setScreenHeight({dynamicHeight: window.innerHeight}));
    });
  }, []);

  const { proposedHeight, containerWidth } = useSizeAndAspectRatio({
    embeddable: props.embeddable,
    ARFromSupportedFeatures,
    heightFromInteractive,
    divSize,
    headerSize,
    screenHeight,
    showDeleteDataButton: false
  });

  useEffect(() => {
    if (scrollToQuestionId === embeddableRefId && divSize) {
      divTarget.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [embeddableRefId, divSize, scrollToQuestionId]);

  const onStateUnavailable = useCallback((error: Error) => {
    console.warn(`Could not load the saved state of ${embeddableRefId}: ${error.message}`);
    // If the state cannot be read before the interactive runs, it never declares, so its gate opens.
    onQuestionGateEventRef.current?.({ type: "stateUnavailable" });
  }, [embeddableRefId]);

  useEffect(() => {
    if (shouldWatchAnswer) {
      return watchAnswer(embeddableRefId, (wrappedAnswer) => {
        answerMeta.current = wrappedAnswer?.meta;
        interactiveState.current = wrappedAnswer?.interactiveState;
        setLoadingAnswer(false);
      }, onStateUnavailable);
    }
  }, [embeddableRefId, shouldWatchAnswer, onStateUnavailable]);

  useEffect(() => {
    if (shouldLoadLegacyLinkedInteractiveState && (laraData.activity || laraData.sequence)) {
      return getLegacyLinkedInteractiveInfo(embeddableRefId, laraData, (info) => {
        legacyLinkedInteractiveState.current = info;
        setLoadingLegacyLinkedInteractiveState(false);
      }, onStateUnavailable);
    }
  }, [embeddableRefId, laraData, shouldLoadLegacyLinkedInteractiveState, onStateUnavailable]);

  useEffect(() => {
    interactiveInfo.current = getInteractiveInfo(laraData, embeddableRefId);
  }, [embeddableRefId, laraData]);

  useEffect(() => {
    let unsubscribe = () => {/* no-op */};
    async function watchFeedback() {
      const answer = await getAnswerMetadata(embeddableRefId);
      if (answer?.id) {
        unsubscribe = watchQuestionLevelFeedback((fbs) => {
          if (fbs?.length) {
            const questionId = refIdToAnswersQuestionId(embeddableRefId);
            const fb = fbs.filter(f => f.questionId === questionId);
            fb.length && setFeedback(fb[0]);
          }
        }, answer.id);
      }
    }
    watchFeedback();

    return () => unsubscribe();
  }, [embeddableRefId]);

  // in older iframe interactive embeddables, we get url, native_width, native_height, etc. directly off
  // of the embeddable object. On newer managed/library interactives, this data is in library_interactive.data.
  const embeddableData = embeddable.type === "ManagedInteractive" ? embeddable.library_interactive?.data : embeddable;

  useEffect(() => {
    setClickToPlayOptions(embeddableData?.click_to_play
    ? ({
        prompt: embeddableData.click_to_play_prompt,
        imageUrl: embeddableData.image_url
      })
    : undefined);
  }, [embeddableData]);

  const previousActiveDialogRef = useRef(activeDialog);
  useEffect(() => {
    const previous = previousActiveDialogRef.current;
    if (previous && !activeDialog) {
      // Dialog just closed. Phase 1 fallback: coarse focus restore on the
      // originating iframe element. Replaced in follow-up AP-108 work by
      // page-level iframe-slot + requestRestore().
      iframeRuntimeRef.current?.getIframeElement()?.focus();
    }
    previousActiveDialogRef.current = activeDialog;
  }, [activeDialog]);

  const handleNewInteractiveState = (state: any) => {
    // Keep interactive state in sync if iFrame is opened in modal popup
    interactiveState.current = state;
    const exportableAnswer = shouldWatchAnswer && getAnswerWithMetadata(state, props.embeddable as IManagedInteractive, answerMeta.current);
    if (exportableAnswer && !isOfferingLocked(portalData)) {
      const interactiveStateHistoryId = saveInteractiveStateHistory ? interactiveStateHistoryIdRef.current : undefined;
      createOrUpdateAnswer(exportableAnswer, interactiveStateHistoryId);
      if (interactiveStateHistoryId) {
        interactiveStateHistoryIdRef.current = nanoid();
      }
    }
  };

  const setNewHint = useCallback((newHint: string) => {
    setHint(newHint);
  }, []);

  const showModal = useCallback((options: IShowModal) => {
    // Difference between dialog and lightbox:
    // - dialog will assume that the provided URL is an interactive, so it'll use iframe-runtime component to render this URL
    // - lightbox does not use iframe-runtime. It just displays URL in a generic iframe or uses <img> tag when `options.isImage` === true
    if (options.type === "dialog") {
      setActiveDialog(options);
    } else if (options.type === "lightbox") {
      setActiveLightbox(options);
    } else {
      window.alert(`${options.type} modal type not implemented yet`);
    }
  }, []);

  const closeModal = useCallback((options: ICloseModal) => {
    // This code should work even if uuid is not provided (undefined).
    if (activeDialog?.uuid === options.uuid) {
      setActiveDialog(null);
    } else if (activeLightbox?.uuid === options.uuid) {
      setActiveLightbox(null);
    }
  }, [activeDialog, activeLightbox]);

  const getFirebaseJWT = useCallback((firebaseApp: string, others: Record<string, any>) => {
    return handleGetFirebaseJWT({ firebase_app: firebaseApp, ...others }, portalData);
  }, [portalData]);

  const { authored_state } = embeddable;
  const questionName = embeddable.name || "";
  const url = embeddableData?.base_url || embeddableData?.url || "";
  const authoredState = useMemo(() => {
    const overridden = typeof authored_state === "string"
      ? applyOverridesToAuthoredState(authored_state)
      : authored_state;
    return safeJsonParseIfString(overridden) || {};
  }, [authored_state]);
  const linkedInteractives = useRef(embeddable.linked_interactives?.length
    ? embeddable.linked_interactives.map(link => ({ id: link.ref_id, label: link.label }))
    : undefined);

  const setShowDeleteDataButton = () => {
    return embeddable.type === "MwInteractive"
             && embeddable.enable_learner_state
             && embeddable.show_delete_data_button
           || embeddable.type === "ManagedInteractive"
             && embeddable.library_interactive?.data.enable_learner_state
             && embeddable.library_interactive.data.show_delete_data_button;
  };

  const showDeleteDataButton = setShowDeleteDataButton();

  const handleHintClose = () => {
    Logger.log({
      event: LogEventName.toggle_hint,
      parameters: { show_hint: false, hint }
    });
    setShowHint(false);
    // Return focus to the "?" trigger so keyboard users aren't dropped to <body>
    // when the panel that contained the close button collapses.
    hintTriggerRef.current?.focus();
  };
  const handleShowHint = () => {
    Logger.log({
      event: LogEventName.toggle_hint,
      parameters: { show_hint: !showHint, hint }
    });
    // Functional update so the toggle is always derived from the latest state,
    // independent of render/flush timing (future-proof against batched updates).
    setShowHint(prevShowHint => !prevShowHint);
  };

  const handleCloseDialog = () => {
    // Request the dialog interactive's current state before closing, but close on either
    // outcome: an interactive that cannot be reached, or that answers "nochange", must not
    // leave the learner shut inside the dialog.
    const close = () => setActiveDialog(null);
    const request = iframeRuntimeRef.current?.saveInteractiveStateBeforeClose();
    if (request) {
      void request.then(close, close);
    } else {
      close();
    }
  };

  const handleCloseLightbox = () => {
    setActiveLightbox(null);
  };

  const getAnswerMetadata = async (answerInteractiveId?: string) => {
    if (answerInteractiveId) {
      const wrappedAnswer = await getAnswer(answerInteractiveId);
      if (wrappedAnswer) {
        return wrappedAnswer.meta;
      }
    }
    return answerMeta.current;
  };

  const handleGetAttachmentUrlRequest = async (request: IAttachmentUrlRequest): Promise<IAttachmentUrlResponse> => {
    // the answerMetadata does not exist for interactives that have never been saved before now
    let answerMetadata: IExportableAnswerMetadata = answerMeta.current || {} as any;
    // normally, the interactiveId is only present when requesting data for a linked interactive, but it's
    // possible an interactive will set interactiveId to be its own ID
    if (request.interactiveId) {
      answerMetadata = (await getAnswerMetadata(request.interactiveId)) || ({} as any);
    }
    return await handleGetAttachmentUrl({
      request,
      answerMeta: answerMetadata,
      writeOptions: {
        interactiveId: embeddableRefId,
        onAnswerMetaUpdate: newMeta => {
          // don't allow writes over passed in interactiveId (for now, until it is needed and thought through...)
          if (!answerMeta.current && request.interactiveId && request.interactiveId !== embeddableRefId) {
            return { error: "writing to another interactive is not allowed", requestId: request.requestId };
          }

          let interactiveStateHistoryId: string | undefined = undefined;
          if (!answerMeta.current) {
            // allow answers that are only attachments (e.g., a recorded audio response)
            answerMeta.current = getAnswerWithMetadata({}, props.embeddable);
            if (saveInteractiveStateHistory) {
              interactiveStateHistoryId = interactiveStateHistoryIdRef.current;
              interactiveStateHistoryIdRef.current = nanoid();
            }
          }

          // note: we only pass an interactiveStateHistoryId when creating a new answer document
          createOrUpdateAnswer({...answerMeta.current, ...newMeta}, interactiveStateHistoryId);
        }
      }
    });
  };

  const handleClickToPlay = () => {
    setClickToPlayOptions(undefined);
    setClickedToPlay(true);
    // in the current Lara code we emit the interactive is available even if the iframe src it not set yet
    props.emitInteractiveAvailable?.();
  };

  const handleLog = (logData: any) => {
    Logger.log({
      event: logData.action,
      event_value: logData.value,
      parameters: logData.data,
      interactive_id: embeddableRefId,
      interactive_url: url,
      interactiveStateHistoryId: saveInteractiveStateHistory ? interactiveStateHistoryIdRef.current : undefined
    });
    // Forward the log into the active page chat (if a tutor sidebar is mounted).
    // Spam-dropped + MC choice-map enriched in the pure builder; a no-op when no chat is present.
    forwardInteractiveLog({ logData, interactiveId: embeddableRefId, interactiveUrl: url, authoredState });
  };

  useImperativeHandle(ref, () => ({
    saveInteractiveStateBeforeClose: (options?: IGetInteractiveState) => {
      if (shouldWatchAnswer && iframeRuntimeRef.current) {
        return iframeRuntimeRef.current.saveInteractiveStateBeforeClose(options);
      } else {
        // Interactive doesn't save state, so return resolved promise immediately.
        return Promise.resolve();
      }
    }
  }));

  // embeddable.url_fragment is an optional string (path, query params, hash) that can be defined by author.
  // Some interactives are authored that way. Note that url_fragment is not merged with the dialog URL.
  // Each interactive will be first loaded inline with the url_fragment appended. So it can merge its custom dialog URL
  // with this fragment if necessary. ActivityPlayer doesn't have knowledge about URL format and provided url_fragment
  // to perform this merge automatically.
  // URL overrides for this iframe are applied at the IframeRuntime render site
  // (src/components/activity-page/managed-interactive/iframe-runtime.tsx), which
  // is the single place every interactive iframe's src is set.
  const iframeUrl = activeDialog?.url || (embeddable.url_fragment ? url + embeddable.url_fragment : url);

  // question numbers are 1-based
  const hasQuestionNumber = (questionNumber || 0) > 0;
  const hasQuestionName = questionName.trim().length > 0;
  const isNotebookLayout = laraData.activity?.layout === ActivityLayouts.Notebook;
  const hideQuestionHeader = (props.hideQuestionNumbers || !hasQuestionNumber) && !hasQuestionName && !hint && !isNotebookLayout && !hasPluginRequiringHeader;
  const hintPanelId = `hint-panel-${embeddableRefId}`;

  const isInteractive = embeddable.type === "MwInteractive";
  const isManagedInteractive = embeddable.type === "ManagedInteractive";
  const hasBorder = isManagedInteractive || (isInteractive && !hideQuestionHeader);
  const className = classNames("runtime-container", {"has-question-number": hasQuestionNumber, "has-border": hasBorder});

  // Common props for IframeRuntime, used by both the inline render and the dialog overlay.
  const iframeRuntimeProps = {
    id: embeddableRefId,
    authoredState,
    initialInteractiveState: interactiveState.current,
    legacyLinkedInteractiveState: legacyLinkedInteractiveState.current,
    setInteractiveState: handleNewInteractiveState,
    setSupportedFeatures,
    linkedInteractives: linkedInteractives.current,
    proposedHeight,
    containerWidth,
    setNewHint,
    getFirebaseJWT,
    getAttachmentUrl: handleGetAttachmentUrlRequest,
    showModal,
    closeModal,
    setSendCustomMessage,
    setNavigation,
    onQuestionGateEvent,
    iframeTitle: questionNumber
      ? `Question ${questionNumber} ${questionName} content`
      : embeddable.name || "Interactive content",
    portalData,
    answerMetadata: answerMeta.current,
    interactiveInfo: interactiveInfo.current,
    showDeleteDataButton,
    setAspectRatio: setARFromSupportedFeatures,
    setHeightFromInteractive,
    hasHeader: !hideQuestionHeader,
    feedback,
    log: handleLog,
    disabled,
  };

  const interactiveIframeRuntime =
    loadingAnswer || loadingLegacyLinkedInteractiveState ?
      "Loading..." :
      <IframeRuntime
        ref={iframeRuntimeRef}
        url={iframeUrl}
        {...iframeRuntimeProps}
      />;

  return (
    <div ref={divTarget} className="managed-interactive" data-cy="managed-interactive">
      <div className={className} style={{width:containerWidth}}>
      <ManagedInteractiveHeader
        questionNumber={displayedQuestionNumber(questionNumber, props.hideQuestionNumbers)}
        questionName={questionName}
        hint={hint}
        showHint={showHint}
        hintPanelId={hintPanelId}
        triggerRef={hintTriggerRef}
        onToggleHint={handleShowHint}
        hideHeader={hideQuestionHeader}
        disabled={disabled}
        locked={locked}
      />
      <ManagedInteractiveHint
        hint={hint}
        showHint={showHint}
        panelId={hintPanelId}
        questionName={questionName}
        onToggleHint={handleHintClose}
      />
      {clickToPlayOptions && !clickedToPlay
        ? <ClickToPlay
            prompt={clickToPlayOptions.prompt}
            imageUrl={clickToPlayOptions.imageUrl}
            onClick={handleClickToPlay}
            disabled={disabled}
          />
        : <>
            { !activeDialog && interactiveIframeRuntime }
            {
              activeDialog && (loadingAnswer || loadingLegacyLinkedInteractiveState
                ? "Loading..."
                : <DialogOverlay
                    url={iframeUrl}
                    title={iframeRuntimeProps.iframeTitle}
                    notCloseable={activeDialog.notCloseable}
                    onClose={handleCloseDialog}
                    iframeRuntimeProps={iframeRuntimeProps}
                    iframeRuntimeRef={iframeRuntimeRef}
                  />)
            }
            {
              activeLightbox && <Lightbox onClose={handleCloseLightbox} {...activeLightbox} />
            }
          </>
      }
      </div>
    </div>
    );
  });
ManagedInteractive.displayName = "ManagedInteractive";
